// Experience categories used by both the admin select and the public filter.
export const experienceCategories = [
  { value: "practice", label_es: "Práctica Profesional", label_en: "Professional Practice" },
  { value: "fullstack", label_es: "Full-stack", label_en: "Full-stack" },
  { value: "frontend", label_es: "Front-end", label_en: "Front-end" },
  { value: "backend", label_es: "Back-end", label_en: "Back-end" },
];

// Project categories used by both the admin select and the public filter.
export const projectCategories = [
  { value: "software", label_es: "Software", label_en: "Software" },
  { value: "webpage", label_es: "Página web", label_en: "Website" },
  { value: "repo", label_es: "Repositorio", label_en: "Repository" },
];

// Study categories used by both the admin select and the public filter.
export const studyCategories = [
  { value: "degree", label_es: "Licenciatura", label_en: "Degree" },
  { value: "master", label_es: "Maestría", label_en: "Master" },
];

// Work modalities for the experience "employment_type" select.
export const employmentTypes = [
  { value: "fulltime", label_es: "Tiempo completo", label_en: "Full-time" },
  { value: "parttime", label_es: "Medio tiempo", label_en: "Part-time" },
  { value: "freelance", label_es: "Freelance", label_en: "Freelance" },
  { value: "contract", label_es: "Contrato", label_en: "Contract" },
  { value: "internship", label_es: "Práctica profesional", label_en: "Internship" },
  { value: "remote", label_es: "Remoto", label_en: "Remote" },
  { value: "hybrid", label_es: "Híbrido", label_en: "Hybrid" },
];

// Localized label for a modality slug (falls back to the raw value).
export function employmentTypeLabel(value, language = "es") {
  const t = employmentTypes.find((x) => x.value === value);
  if (!t) return value || "";
  return (language === "es" ? t.label_es : t.label_en) || value;
}

// Tablas que se muestran "lo último cargado primero" (orden descendente):
// al insertar, cada fila recibe order = maxOrder+1, así que el mayor va arriba.
export const NEWEST_FIRST_TABLES = ["experience", "studies"];
export const orderAscending = (table) => !NEWEST_FIRST_TABLES.includes(table);

// Skill categories used by the admin select and the public grouping/order.
export const skillCategories = [
  { value: "frontend", label_es: "Frontend", label_en: "Frontend" },
  { value: "backend", label_es: "Backend", label_en: "Backend" },
  { value: "mobile", label_es: "Móvil", label_en: "Mobile" },
  { value: "database", label_es: "Bases de datos", label_en: "Databases" },
  { value: "packages", label_es: "Paquetes", label_en: "Packages" },
  { value: "other", label_es: "Otros", label_en: "Other" },
];

// Each field has a human, bilingual `label` ("Español / English"). Fields typed
// "bilingual"/"bilingual-textarea" render a Spanish AND English input. Other
// types (text/month/select/image/csv/checkbox) hold a single value that does not
// need translation (dates, names, categories, images, URLs).
export const collectionsConfig = {
  studies: {
    label: "Estudios",
    fields: [
      { key: "title", type: "bilingual", label: "Título / Title", required: true },
      { key: "institution", type: "text", label: "Institución / Institution", required: true },
      {
        key: "category",
        type: "select",
        label: "Tipo / Type",
        required: true,
        optionsFrom: "studies",
        options: studyCategories.map((c) => ({
          value: c.value,
          label: `${c.label_es} / ${c.label_en}`,
        })),
      },
      { key: "image_url", type: "image", label: "Logo / Logo" },
      { key: "date_start", type: "month", label: "Fecha de inicio / Start date" },
      { key: "date_end", type: "month", label: "Fecha de fin / End date" },
      { key: "current", type: "checkbox", label: "Actualidad (en curso) / Present (ongoing)" },
      { key: "description", type: "bilingual-textarea", label: "Descripción / Description" },
      { key: "order", type: "number", label: "Orden / Order", help: "Se ordena de lo más reciente a lo más antiguo (número mayor = aparece primero). Se autoasigna al crear." },
    ],
  },
  experience: {
    label: "Experiencia",
    fields: [
      { key: "role", type: "bilingual", label: "Cargo / Role", required: true },
      { key: "company", type: "text", label: "Empresa / Company", required: true },
      { key: "url", type: "text", label: "URL de la empresa / Company URL", help: "Opcional. Si la pones, el nombre de la empresa se vuelve un enlace." },
      { key: "location", type: "text", label: "Ubicación / Location", help: "Opcional. Ej. Ciudad de Panamá, Panamá." },
      {
        key: "employment_type",
        type: "select",
        label: "Modalidad / Work type",
        options: employmentTypes.map((t) => ({
          value: t.value,
          label: `${t.label_es} / ${t.label_en}`,
        })),
      },
      { key: "image_url", type: "image", label: "Logo de empresa / Company logo" },
      {
        key: "category",
        type: "select",
        label: "Categoría / Category",
        required: true,
        optionsFrom: "experience",
        options: experienceCategories.map((c) => ({
          value: c.value,
          label: `${c.label_es} / ${c.label_en}`,
        })),
      },
      { key: "date_start", type: "month", label: "Fecha de inicio / Start date" },
      { key: "date_end", type: "month", label: "Fecha de fin / End date" },
      { key: "current", type: "checkbox", label: "Actualidad (en curso) / Present (ongoing)" },
      { key: "bullets", type: "bilinguallist", label: "Descripción (lista de puntos) / Description (bullet list)" },
      { key: "order", type: "number", label: "Orden / Order", help: "Se ordena de lo más reciente a lo más antiguo (número mayor = aparece primero). Se autoasigna al crear." },
    ],
  },
  projects: {
    label: "Proyectos",
    fields: [
      { key: "title", type: "bilingual", label: "Título / Title", required: true },
      { key: "description", type: "bilingual-textarea", label: "Descripción / Description" },
      {
        key: "category",
        type: "select",
        label: "Categoría / Category",
        required: true,
        optionsFrom: "projects",
        options: projectCategories.map((c) => ({
          value: c.value,
          label: `${c.label_es} / ${c.label_en}`,
        })),
      },
      { key: "tags", type: "techtags", label: "Tecnologías / Technologies" },
      { key: "images", type: "images", label: "Imágenes (carrusel) / Images (carousel)" },
      { key: "links", type: "links", label: "Enlaces / Links" },
      { key: "order", type: "number", label: "Orden / Order", help: "Se ordena de lo más reciente a lo más antiguo (número mayor = aparece primero). Se autoasigna al crear." },
    ],
  },
  skills: {
    label: "Habilidades",
    fields: [
      { key: "name", type: "text", label: "Nombre / Name", required: true },
      { key: "icon", type: "iconpicker", label: "Ícono / Icon" },
      {
        key: "category",
        type: "select",
        label: "Categoría / Category",
        required: true,
        optionsFrom: "skills",
        options: skillCategories.map((c) => ({
          value: c.value,
          label: `${c.label_es} / ${c.label_en}`,
        })),
      },
    ],
  },
  // Editable categories (per scope), managed from the admin "Categorías" section.
  categories: {
    label: "Categorías",
    sortByScope: true,
    listLabel: (row) => {
      const scopes = {
        experience: "Experiencia",
        projects: "Proyectos",
        studies: "Estudios",
        skills: "Habilidades",
      };
      const scope = scopes[row.scope] || row.scope || "—";
      const name = row.label_es || row.value || "";
      return `[${scope}]  ${name}`;
    },
    fields: [
      {
        key: "scope",
        type: "select",
        label: "Sección / Section",
        required: true,
        options: [
          { value: "experience", label: "Experiencia / Experience" },
          { value: "projects", label: "Proyectos / Projects" },
          { value: "studies", label: "Estudios / Education" },
          { value: "skills", label: "Habilidades / Skills" },
        ],
      },
      { key: "value", type: "text", label: "Clave / Key (slug, ej. frontend)", required: true },
      { key: "label", type: "bilingual", label: "Etiqueta / Label", required: true },
    ],
  },
  languages: {
    label: "Idiomas",
    fields: [
      { key: "name", type: "bilingual", label: "Idioma / Language", required: true },
      { key: "level", type: "bilingual", label: "Nivel / Level", required: true },
    ],
  },
  certifications: {
    label: "Certificados",
    fields: [
      { key: "name", type: "bilingual", label: "Nombre / Name", required: true },
      { key: "issuer", type: "text", label: "Emisor / Issuer", required: true },
      { key: "image_url", type: "image", label: "Imagen / Image" },
    ],
  },
};
